import { Component, OnInit, ViewChild } from "@angular/core";
import { CommonModule } from "@angular/common";
import { ReactiveFormsModule } from "@angular/forms";
import { catchError, finalize } from "rxjs";
import { ToastrService } from "ngx-toastr";

// NG-ZORRO Imports
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzListModule } from 'ng-zorro-antd/list';
import { NzBreadCrumbModule } from 'ng-zorro-antd/breadcrumb';
import { NzGridModule } from 'ng-zorro-antd/grid';

import { Breadcrumb, GoogleFileDto, MediaTypeEnum, PermissionEnum, ProductDto, ProductStatusEnum } from "../../../dto";
import { HttpFilesService } from "../../../services/files/http-files.service";
import { NzDividerComponent, NzDividerModule } from "ng-zorro-antd/divider";
import { HttpProductService } from "../../../services/product/http-product.service";
import { ProductActionService } from "./product-action.service";
import { NzDropDownModule } from "ng-zorro-antd/dropdown";
import { ProductEditorModalComponent } from "../product-editor/product-editor-modal.component";
import { NzTypographyModule } from "ng-zorro-antd/typography";
import { ProductPackagesModalComponent } from "../../../components/product-packages/product-packages-modal.component";
import { AuthService } from "../../../services/auth/auth.service";



@Component({
    selector: 'app-view-product',
    standalone: true,
    imports: [
        CommonModule, 
        ReactiveFormsModule, 
        NzTableModule,
        NzButtonModule,
        NzIconModule,
        NzCardModule,
        NzListModule,
        NzBreadCrumbModule,
        NzGridModule,
        NzDividerModule,
        NzDropDownModule,
        ProductEditorModalComponent,
        ProductPackagesModalComponent,
        NzTypographyModule
    ],
    templateUrl: './view-product.component.html',
    styleUrls: ['./view-product.component.scss']
})
export class ViewProductComponent implements OnInit {

    products: ProductDto[] = [];
    selectedProduct!: ProductDto;
    ProductStatusEnum = ProductStatusEnum;
    loading = false;

    expandedDescriptions = new Set<number>();

    mediaTypeEnum = MediaTypeEnum;
    PermissionEnum = PermissionEnum;

    @ViewChild(ProductEditorModalComponent) productModalComponent!: ProductEditorModalComponent;
    @ViewChild(ProductPackagesModalComponent) packagesModalComponent!: ProductPackagesModalComponent;

    constructor(
        private toastr: ToastrService, 
        private httpService: HttpProductService, 
        private actionService: ProductActionService,
        public authService: AuthService
    ) { }

    ngOnInit(): void {
        this.fetchAllProducts();
    }

    fetchAllProducts() {
        const search: ProductDto = {};
        this.loading = true;

        this.httpService.getAllProducts(search)
            .pipe(
                catchError(error => {
                    console.error('Error fetching products', error);
                    this.toastr.error('Failed to fetch product: ' + (error.error?.error || error.message));
                    throw error;
                }),
                finalize(() => this.loading = false)
            )
            .subscribe(data => {
                this.products = data;
            });
    }

    async disable(input: ProductDto) {
        const success = await this.actionService.disableProduct(input);
        if (success) this.fetchAllProducts();
    }

    async enable(input: ProductDto) {
        const success = await this.actionService.enableProduct(input);
        if (success) this.fetchAllProducts();
    }

    addProduct() {
        this.productModalComponent.open();
    }

    editProduct(input: ProductDto) {
        this.selectedProduct = input;
        this.productModalComponent.open(input);
    }

    onSave() {
        this.fetchAllProducts();
    }

    viewMedia(product: ProductDto, mediaType: MediaTypeEnum): void {
        let driveLink: string | undefined;

        // Route to the correct drive link based on the requested media type
        switch (mediaType) {
            case MediaTypeEnum.ProductBrochurePdf:
                driveLink = product.brochureDriveFile?.driveFileLink;
                break;
            case MediaTypeEnum.ProductDatasheetPdf:
                driveLink = product.datasheetDriveFile?.driveFileLink;
                break;
            case MediaTypeEnum.ProductImage:
                driveLink = product.logoDriveFile?.driveFileLink;
                break;
        }

        if (driveLink) {
            window.open(driveLink, '_blank');
        } else {
            this.toastr.error('Could not find the link for this media.');
        }
    }

    toggleDescription(productId: number): void {
        if (this.expandedDescriptions.has(productId)) {
            this.expandedDescriptions.delete(productId);
        } else {
            this.expandedDescriptions.add(productId);
        }
    }

    managePackages(product: ProductDto) {
        this.packagesModalComponent.open(product);
    }
}